import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isFeatureEnabled } from "@/lib/features";
import { B2B_PAGINA_VISIVEL } from "@/lib/featureFlags";
import { podeVerEmpresas, destinoSubrotaEmpresas } from "@/lib/previewLovable";
import { isNativePlatform } from "@/lib/platform";
import { useMontarDepois } from "@/lib/montarDepois";
import { usePageView } from "@/hooks/usePageView";

/**
 * Casca mínima das páginas públicas (home, /parceiras, /empresas, textos legais
 * e as /lp/).
 *
 * Essas páginas são lidas por quem ainda não tem conta: não há sessão, plano,
 * dia, emoções nem paywall. Montadas dentro do `App.tsx`, carregavam a árvore
 * inteira de contextos junto — e o navegador baixava o pacote do app logado
 * (mais de 300 KB) antes de mostrar a primeira tela. Aqui só entram o roteador,
 * a página pedida e o aviso de cookies (obrigatório: o Pixel só dispara com
 * consentimento), este último depois da primeira tela.
 *
 * Quem já tem sessão guardada, o app da loja e qualquer outro caminho seguem
 * pelo `App.tsx` completo — a escolha acontece no `main.tsx`.
 */
const Landing = lazy(() => import("@/pages/Landing"));
const LandingPersona = lazy(() => import("@/pages/LandingPersona"));
const Parceiras = lazy(() => import("@/pages/Parceiras"));
const B2BLanding = lazy(() => import("@/pages/B2BLanding"));
const TermosUso = lazy(() => import("@/pages/TermosUso"));
const PoliticaPrivacidade = lazy(() => import("@/pages/PoliticaPrivacidade"));
const PoliticaCookies = lazy(() => import("@/pages/PoliticaCookies"));
const BrincadeirasParaCriancas = lazy(() => import("@/pages/BrincadeirasParaCriancas"));
const CookieBanner = lazy(() =>
  import("@/components/cookie-consent/CookieBanner").then((m) => ({ default: m.CookieBanner })),
);
const AppRedirect = lazy(() => import("@/pages/AppRedirect"));
const AppDownloadBanner = lazy(() => import("@/components/app-banner/AppDownloadBanner"));
/**
 * Barra de rodapé do app — entra depois da primeira tela, como o aviso.
 * Exceção por rota (23/09/2026): a /brincadeiras-para-criancas recebe tráfego
 * pago de busca cujo objetivo é cadastro no site, e a barra leva à loja — nela
 * ela vira perda. Nas demais páginas públicas e na /entrada segue igual; o
 * componente não mudou.
 */
const BannerDoApp: React.FC = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/brincadeiras-para-criancas")) return null;
  // /empresas (30/10/2026): a página tem o próprio botão fixo de cotação no
  // celular — a barra de instalar o app ficaria embaixo dele.
  if (pathname.startsWith("/empresas")) return null;
  return <AppDownloadBanner />;
};

const Carregando = () => (
  <div className="min-h-[100dvh] bg-background flex items-center justify-center" aria-hidden="true">
    <div className="w-8 h-8 rounded-full border-2 border-brand/25 border-t-brand animate-spin" />
  </div>
);

/** Sai da casca leve para o app completo, preservando caminho e parâmetros. */
const DevolverAoApp: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    window.location.assign(`${location.pathname}${location.search}${location.hash}`);
  }, [location]);
  return <Carregando />;
};

/**
 * Registra a visita (UTM, public_visits e app_open) dentro do roteador.
 * Sem isso, a casca leve deixava a home e as demais páginas públicas fora da
 * medição de tráfego — justamente as páginas de destino dos anúncios.
 */
const RegistroDeVisita: React.FC = () => {
  usePageView();
  return null;
};

const PublicoApp: React.FC = () => {
  const mostrarAviso = useMontarDepois();
  return (
    <BrowserRouter>
      <RegistroDeVisita />
      <Suspense fallback={<Carregando />}>
        <Routes>
          {/* A home tem variante atrás de flag; ligada, o app completo assume. */}
          <Route
            path="/"
            element={isFeatureEnabled("entradaAgendaPrimeiro") ? <DevolverAoApp /> : <Landing />}
          />
          <Route path="/lp/:persona" element={<LandingPersona />} />
          {/* /app — redirecionador de loja; casca leve porque é destino de bio e anúncio. */}
          <Route path="/app" element={<AppRedirect />} />
          <Route path="/parceiras" element={<Parceiras />} />
          <Route path="/empresas" element={podeVerEmpresas(B2B_PAGINA_VISIVEL, isNativePlatform(), window.location.hostname) ? <B2BLanding /> : <Navigate to="/" replace />} />
          <Route path="/empresas/*" element={<Navigate to={destinoSubrotaEmpresas(B2B_PAGINA_VISIVEL, isNativePlatform(), window.location.hostname)} replace />} />
          <Route path="/termos" element={<TermosUso />} />
          <Route path="/termos-uso" element={<TermosUso />} />
          <Route path="/privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/politica-de-privacidade" element={<Navigate to="/privacidade" replace />} />
          <Route path="/politica-cookies" element={<PoliticaCookies />} />
          {/* Página de conteúdo aberta a todas, inclusive com sessão. */}
          <Route path="/brincadeiras-para-criancas" element={<BrincadeirasParaCriancas />} />
          <Route path="*" element={<DevolverAoApp />} />
        </Routes>
      </Suspense>
      {mostrarAviso && (
        <Suspense fallback={null}>
          <CookieBanner />
          <BannerDoApp />
        </Suspense>
      )}
    </BrowserRouter>
  );
};

export default PublicoApp;
